/**
 * Curated hotels linked to destinations (LiteAPI seed + editorial blurbs).
 */
import React from "react";
import { buildCollection } from "@firecms/core";
import { TranslateIcon, RestoreIcon } from "@firecms/ui";
import { getApps } from "firebase/app";
import { doc, getFirestore, serverTimestamp, updateDoc } from "firebase/firestore";
import { editorOrAdminPermissions } from "../permissions";
import { CONTENT_LOCALES, type ContentLocale, LOCALE_LABELS } from "../constants";
import { CONTENT_STATUS_ENUM } from "../lib/contentStatus";
import {
  STATUS_FIELD_DESCRIPTION,
  publishedSnapshotProperties,
} from "../lib/publishedSnapshotProperties";
import {
  applyPublishedSnapshot,
  hasPublishedSnapshot,
} from "../lib/publishedSnapshot";
import { collectHotelCompletenessIssues } from "../lib/completenessValidation";
import { createStatusPreSave } from "../lib/statusPreSave";

function createHotelLocaleContentProperties(locale: ContentLocale) {
  return {
    name: LOCALE_LABELS[locale],
    dataType: "map" as const,
    properties: {
      blurb: {
        name: "Blurb",
        dataType: "string" as const,
        multiline: true,
        description:
          locale === "en"
            ? "Short editorial blurb for destination pages and SRL. English is required for Ready. Use Actions → Translate all for DE/FR/IT."
            : "Localized blurb (Translate all from English, or edit manually).",
      },
      headline: {
        name: "Headline",
        dataType: "string" as const,
        description: "Optional short headline above the blurb.",
      },
    },
  };
}

export const hotelsCollection = buildCollection({
  id: "hotels",
  name: "Hotels",
  singularName: "Hotel",
  path: "hotels",
  icon: "Hotel",
  group: "Content",
  permissions: editorOrAdminPermissions,
  callbacks: {
    onPreSave: createStatusPreSave({
      collectCompleteness: collectHotelCompletenessIssues,
      entityLabel: "hotel",
    }),
  },
  entityActions: [
    {
      key: "hotel_translate_all",
      name: "Translate all",
      icon: <TranslateIcon />,
      collapsed: false,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}/api/hotels/translate`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ document_id: entity.id, mode: "force" }),
          });
          const data = (await res.json()) as {
            ok?: boolean;
            message?: string;
            error?: string;
            skipped?: boolean;
            status_demoted?: boolean;
          };
          if (res.ok && data.ok) {
            snackbar.open({
              type: data.skipped ? "info" : "success",
              message: data.message ??
                (data.status_demoted
                  ? "Translated. Status is Live · Draft — mark Live · Ready and Global Publish."
                  : "Translated. Reload the entity to see DE/FR/IT blurbs."),
            });
          } else {
            snackbar.open({
              type: "error",
              message: data.error ?? data.message ?? "Hotel translation failed",
            });
          }
        } catch (err) {
          snackbar.open({
            type: "error",
            message: err instanceof Error ? err.message : "Hotel translation request failed",
          });
        }
      },
    },
    {
      key: "hotel_fill_empty_translations",
      name: "Fill empty translations",
      icon: <TranslateIcon />,
      collapsed: false,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}/api/hotels/translate`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ document_id: entity.id, mode: "fill_empty" }),
          });
          const data = (await res.json()) as {
            ok?: boolean;
            message?: string;
            error?: string;
            skipped?: boolean;
          };
          if (res.ok && data.ok) {
            snackbar.open({
              type: data.skipped ? "info" : "success",
              message: data.message ?? "Empty hotel locales filled. Reload the entity.",
            });
          } else {
            snackbar.open({
              type: "error",
              message: data.error ?? data.message ?? "Hotel translation failed",
            });
          }
        } catch (err) {
          snackbar.open({
            type: "error",
            message: err instanceof Error ? err.message : "Hotel translation request failed",
          });
        }
      },
    },
    {
      key: "hotel_revert_to_live",
      name: "Revert to live",
      icon: <RestoreIcon />,
      collapsed: false,
      onClick: async ({ entity, context }) => {
        if (!entity?.id) return;
        const snackbar = context.snackbarController;
        const values = (entity.values ?? {}) as Record<string, unknown>;
        if (!hasPublishedSnapshot(values)) {
          snackbar.open({
            type: "info",
            message: "No live snapshot yet. Publish once to enable revert.",
          });
          return;
        }
        const status = values.status;
        if (status !== "published_draft" && status !== "published_ready") {
          snackbar.open({
            type: "info",
            message: "Revert is for Live · Draft / Live · Ready items with unpublished edits.",
          });
          return;
        }
        const app = getApps()[0];
        if (!app) {
          snackbar.open({ type: "error", message: "Firebase is not initialized." });
          return;
        }
        try {
          const restored = applyPublishedSnapshot(
            values,
            values.published_snapshot as Record<string, unknown>,
          );
          const { status: _s, updated_at: _u, created_at: _c, ...fields } = restored;
          await updateDoc(doc(getFirestore(app), entity.path, entity.id), {
            ...fields,
            status: "published",
            updated_at: serverTimestamp(),
          });
          snackbar.open({
            type: "success",
            message: "Reverted to live snapshot. Reload the entity to see restored fields.",
          });
        } catch (err) {
          snackbar.open({
            type: "error",
            message: err instanceof Error ? err.message : "Revert failed",
          });
        }
      },
    },
  ],
  properties: {
    liteapi_hotel_id: {
      name: "LiteAPI hotel ID",
      dataType: "string",
      validation: { required: true },
      description: "Stable LiteAPI / Nuitee hotel identifier.",
    },
    destination_id: {
      name: "Destination ID",
      dataType: "string",
      validation: { required: true },
      description: "Firestore document id of the linked destination.",
    },
    slug: {
      name: "Slug",
      dataType: "string",
      description: "URL-safe slug (optional).",
    },
    enabled: {
      name: "Enabled",
      dataType: "boolean",
      defaultValue: true,
      description:
        "Off = hidden from destination pages and SRL priority. Turn back On to sell again; seed/sync will not flip this for you.",
    },
    name: {
      name: "Name",
      dataType: "string",
      validation: { required: true },
    },
    address: {
      name: "Address",
      dataType: "string",
    },
    zip: {
      name: "ZIP / postal code",
      dataType: "string",
    },
    city: {
      name: "City",
      dataType: "string",
    },
    country: {
      name: "Country",
      dataType: "string",
    },
    latitude: {
      name: "Latitude",
      dataType: "number",
      readOnly: true,
      description: "Synced from LiteAPI (top-level or location.*). Re-seed the city if empty.",
    },
    longitude: {
      name: "Longitude",
      dataType: "number",
      readOnly: true,
      description: "Synced from LiteAPI (top-level or location.*). Re-seed the city if empty.",
    },
    star_rating: {
      name: "Star rating",
      dataType: "number",
    },
    guest_rating: {
      name: "Guest rating",
      dataType: "number",
    },
    review_count: {
      name: "Review count",
      dataType: "number",
    },
    checkin_checkout: {
      name: "Check-in / check-out",
      dataType: "map",
      properties: {
        checkin: {
          name: "Check-in",
          dataType: "string",
        },
        checkout: {
          name: "Check-out",
          dataType: "string",
        },
      },
    },
    hotel_type: {
      name: "Hotel type",
      dataType: "string",
    },
    chain_id: {
      name: "Chain ID",
      dataType: "string",
    },
    facilities: {
      name: "Facilities",
      dataType: "array",
      of: {
        dataType: "map",
        properties: {
          facility_id: {
            name: "Facility ID",
            dataType: "number",
          },
          name: {
            name: "Name",
            dataType: "string",
          },
        },
      },
    },
    facility_names: {
      name: "Facility names",
      dataType: "array",
      of: { dataType: "string" },
      description: "Denormalized facility labels for search / filters.",
    },
    rooms_summary: {
      name: "Rooms summary",
      dataType: "array",
      description: "Optional room sketches from sync (flexible fields).",
      of: {
        dataType: "map",
        properties: {
          name: {
            name: "Name",
            dataType: "string",
          },
          size: {
            name: "Size",
            dataType: "string",
          },
          occupancy: {
            name: "Occupancy",
            dataType: "number",
          },
        },
      },
    },
    main_photo_url: {
      name: "Main photo URL",
      dataType: "string",
    },
    image_urls: {
      name: "Image URLs",
      dataType: "array",
      of: {
        dataType: "string",
      },
      description: "Remote LiteAPI gallery URLs (not downloaded to Storage).",
    },
    source_description: {
      name: "Source description",
      dataType: "string",
      multiline: true,
      description: "Raw provider description from LiteAPI sync.",
    },
    source_fingerprint: {
      name: "Source fingerprint",
      dataType: "string",
      description: "Hash of last synced provider payload.",
    },
    source_synced_at: {
      name: "Source synced at",
      dataType: "date",
    },
    source_missing: {
      name: "Source missing",
      dataType: "boolean",
      description: "True when the hotel disappeared from the provider feed.",
    },
    blurb_locked: {
      name: "Blurb locked",
      dataType: "boolean",
      description: "When true, seed/sync must not overwrite editorial blurbs.",
    },
    needs_review: {
      name: "Needs review",
      dataType: "boolean",
      description: "Set when seed or translate rewrites copy — review before Live · Ready.",
    },
    content: {
      name: "Content",
      dataType: "map",
      description:
        "Localized editorial content: { en/de/fr/it } with blurb + optional headline. Translate all fills DE/FR/IT from English.",
      properties: Object.fromEntries(
        CONTENT_LOCALES.map((locale) => [
          locale,
          createHotelLocaleContentProperties(locale),
        ]),
      ),
    },
    soft_tags: {
      name: "Soft tags",
      dataType: "array",
      of: { dataType: "string" },
    },
    status: {
      name: "Status",
      dataType: "string",
      enumValues: CONTENT_STATUS_ENUM,
      description: STATUS_FIELD_DESCRIPTION,
    },
    ...publishedSnapshotProperties,
    created_at: {
      name: "Created At",
      dataType: "date",
      autoValue: "on_create",
    },
    updated_at: {
      name: "Updated At",
      dataType: "date",
      autoValue: "on_update",
    },
  },
});
